import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";
import { Modal } from "@/components/ui/Overlay";
import { Field, FormSuccess, TextArea, TextInput } from "@/components/ui/Form";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/ui/Chips";
import { SafeImage } from "@/components/ui/Blocks";
import { useEnquiryMutation, useTurnkeyProjects } from "@/hooks";
import { CTA_BY_STATUS } from "@/lib/constants";
import { toast } from "@/store/toasts";
import type { TurnkeyProject } from "@/types";

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your name"),
  phone: z
    .string()
    .trim()
    .regex(/^[+]?[\d\s\-()]{10,16}$/, "Enter a valid phone number"),
  email: z.string().trim().email("Enter a valid email address"),
  message: z.string().trim().optional(),
});

type FormValues = z.infer<typeof schema>;

interface EnquiryModalProps {
  project: TurnkeyProject | null;
  onClose: () => void;
}

/**
 * The enquiry flow adapts to the development's status. A sold-out project does
 * not show a form at all — it shows what the visitor can actually buy.
 */
export function EnquiryModal({ project, onClose }: EnquiryModalProps) {
  const soldOut = project?.status === "sold-out";
  const cta = project ? CTA_BY_STATUS[project.status] : null;

  const similar = useTurnkeyProjects({ sort: "status" });
  const alternatives = (similar.data ?? [])
    .filter((p) => p.id !== project?.id && p.status !== "sold-out")
    .slice(0, 3);

  const mutation = useEnquiryMutation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const close = () => {
    onClose();
    // Reset after the exit animation so the form does not flash empty.
    window.setTimeout(() => {
      reset();
      mutation.reset();
    }, 260);
  };

  const onSubmit = handleSubmit(async (values) => {
    await mutation.mutateAsync(
      {
        ...values,
        message: values.message ?? "",
        enquiryType: "Turn-Key Project",
        project: project?.name ?? "",
      },
      {
        onSuccess: () => toast.success("Enquiry received", "We will be in touch within one working day."),
        onError: () => toast.error("Could not send", "Please try again in a moment."),
      },
    );
  });

  if (!project) return null;

  if (soldOut) {
    return (
      <Modal
        open
        onClose={close}
        title="Explore similar projects"
        subtitle={`${project.name} is fully sold. These developments are closest in type and location.`}
      >
        <div className="stack">
          {alternatives.map((item) => (
            <Link
              key={item.id}
              to={`/turnkey/${item.id}`}
              className="result-item"
              style={{ gridTemplateColumns: "96px minmax(0,1fr)" }}
              onClick={close}
            >
              <div className="result-item__media">
                <SafeImage src={item.cover} alt={item.name} />
              </div>
              <div className="result-item__body">
                <StatusBadge status={item.status} />
                <h3 style={{ fontSize: ".98rem" }}>{item.name}</h3>
                <p>
                  {item.location} · from {item.priceLabel}
                </p>
              </div>
            </Link>
          ))}
        </div>

        <Link className="btn btn--primary btn--block mt-6" to="/turnkey" onClick={close}>
          See all turn-key projects
        </Link>
      </Modal>
    );
  }

  return (
    <Modal open onClose={close} title={cta!.label} subtitle={`${project.name} · ${project.location}`}>
      {mutation.isSuccess ? (
        <FormSuccess
          title="Thank you — we have your enquiry"
          message="A member of our team will call you within one working day. For anything urgent, reach us on +91 124 456 7890."
        >
          <Button variant="ghost" size="sm" onClick={close}>
            Close
          </Button>
        </FormSuccess>
      ) : (
        <form className="form" onSubmit={onSubmit} noValidate>
          <div className="form-row">
            <Field label="Full name" htmlFor="enqName" required error={errors.name?.message}>
              <TextInput
                id="enqName"
                placeholder="Your name"
                autoComplete="name"
                error={errors.name?.message}
                {...register("name")}
              />
            </Field>

            <Field label="Phone" htmlFor="enqPhone" required error={errors.phone?.message}>
              <TextInput
                id="enqPhone"
                type="tel"
                placeholder="+91 90000 00000"
                autoComplete="tel"
                error={errors.phone?.message}
                {...register("phone")}
              />
            </Field>
          </div>

          <Field label="Email" htmlFor="enqEmail" required error={errors.email?.message}>
            <TextInput
              id="enqEmail"
              type="email"
              placeholder="you@company.com"
              autoComplete="email"
              error={errors.email?.message}
              {...register("email")}
            />
          </Field>

          <Field label="Message" htmlFor="enqMessage">
            <TextArea
              id="enqMessage"
              rows={3}
              placeholder="Anything you would like us to know"
              {...register("message")}
            />
          </Field>

          <Button type="submit" variant="primary" block icon="arrow-right" loading={isSubmitting || mutation.isPending}>
            {cta!.label}
          </Button>

          <p className="small text-center" style={{ margin: 0 }}>
            Prototype only — nothing is submitted anywhere yet.
          </p>
        </form>
      )}
    </Modal>
  );
}
